import { fromBaseClone, fromBaseRecord } from './from-base-context';
import type { FromBaseRow as Row } from './from-base-context';
import type { FromBasePlayerResourceMetadata } from './from-base-player-resources';
import { createFromBaseNpcGrantContext } from './from-base-npc-stages';
import { createDynamicIntegerSourceCollector } from './dynamic-integer-sources';
import { statusIntegerFamilyComputedByWalk } from './integer-layer-rollup';
import { phpJsonInt, phpJsonTruthy } from './php-json-values';

/** ft_apply_feat_integer_layer_contributions, after ability materialization.
 * The corrective integer pass carries these source rows and honors gear locks. */
export function constructNpcFeatIntegersFromBase(base: Row, metadata: FromBasePlayerResourceMetadata, input: Row): Row {
    const host = fromBaseClone(input), { deps, readers } = createFromBaseNpcGrantContext(base, metadata, host);
    const collector = createDynamicIntegerSourceCollector({ ...deps, metadata: metadata.rollup,
        inventoryAbilityTypes: metadata.rollup.inventoryAbilityTypes });
    const feats = collector.resolvedFeats(host);
    if (!feats.length) return host;
    const creature = ((host.computed ??= {}) as Row).creature as Row;
    const modifiers = fromBaseRecord(creature.modifiers) ? creature.modifiers : {};
    const sources = fromBaseRecord(creature.modifier_sources) ? creature.modifier_sources : {};
    for (const layer of metadata.rollup.integerLayers) {
        if (!statusIntegerFamilyComputedByWalk(layer, metadata.rollup)) continue;
        const contribution = collector.featContribution(host, layer, feats);
        if (phpJsonInt(contribution.total) === 0 && !contribution.sources.length) continue;
        const previous = sources[layer];
        const kept = (previous !== null && typeof previous === 'object' ? Object.values(previous) : []).filter(fromBaseRecord)
            .filter(row => readers.itemField(row, 'kind') !== 'feat');
        if (kept.some(row => readers.itemField(row, 'kind') === 'inventory_equipped'
            && readers.itemField(row, 'value_brace') !== null && !phpJsonTruthy(readers.itemField(row, 'suppressed')))) continue;
        const total = phpJsonInt(readers.itemField(modifiers, layer)) + phpJsonInt(contribution.total);
        if (total === 0) delete modifiers[layer]; else { modifiers[layer] = total; creature.modifiers = modifiers; }
        const merged = [...kept, ...contribution.sources];
        if (merged.length) { sources[layer] = merged; creature.modifier_sources = sources; } else delete sources[layer];
    }
    return host;
}
